import React, { useState } from "react";
import { ITabReport } from "redux/app/logs/@types";
import classNames from "classnames";
import moment from "moment";

interface IDeleteReportFormProps {
    report: ITabReport;
    onClose: () => void;
    onSubmit: (data: any) => void;
}

// Interface for tracking nested items selection
interface SelectionItem {
    reportIndex: number;
    cargoIndex?: number; // Optional for cargo reports
}

// Interface for standalone forms
interface StandaloneFormSelection {
    delayOffHireNotice: boolean;
    bunker: boolean;
    bunkerIndices: number[];
    statementOfFacts: boolean;
}

const DeleteReportForm: React.FC<IDeleteReportFormProps> = ({
    report,
    onClose,
    onSubmit,
}) => {
    const [selectedItems, setSelectedItems] = useState<SelectionItem[]>([]);
    const [selectedStandaloneForms, setSelectedStandaloneForms] = useState<StandaloneFormSelection>({
        delayOffHireNotice: false,
        bunker: false,
        bunkerIndices: [],
        statementOfFacts: false
    });
    const [error, setError] = useState<string | null>(null);
    const [expandedDepartures, setExpandedDepartures] = useState<number[]>([]);

    // Convert selected items to the required format for deletion
    const getSelectedIndices = () => {
        return selectedItems.map(item => item.reportIndex);
    };

    // Check if a departure report has cargo reports
    const hasCargoReports = (timelineItem: any, index: number) => {
        return (
            timelineItem.report_type === "DEPARTURE" &&
            timelineItem.isLatest === true &&
            Array.isArray(timelineItem.cargoReport) &&
            timelineItem.cargoReport.length > 0
        );
    };

    // Toggle expansion state of a departure report
    const toggleExpand = (index: number) => {
        setExpandedDepartures(prev => 
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    // Check if a departure report is expanded
    const isExpanded = (index: number) => {
        return expandedDepartures.includes(index);
    };

    // Check if an item is selected
    const isSelected = (reportIndex: number, cargoIndex?: number) => {
        return selectedItems.some(item => 
            item.reportIndex === reportIndex && item.cargoIndex === cargoIndex
        );
    };

    // Check if a departure report has any cargo reports selected
    const hasSelectedCargo = (departureIndex: number) => {
        return selectedItems.some(item => 
            item.reportIndex === departureIndex && item.cargoIndex !== undefined
        );
    };

    // Check if all cargo reports in a departure are selected
    const areAllCargoSelected = (departureIndex: number, cargoCount: number) => {
        const selectedCount = selectedItems.filter(item => 
            item.reportIndex === departureIndex && item.cargoIndex !== undefined
        ).length;
        return selectedCount === cargoCount;
    };

    // Toggle selection of an item (report or cargo)
    const toggleSelection = (reportIndex: number, cargoIndex?: number) => {
        const key = cargoIndex !== undefined 
            ? `${reportIndex}-${cargoIndex}` 
            : reportIndex.toString();
        
        const timeline = report.timeline || [];
        const currentItem = timeline[reportIndex];
        const hasCargoItems = hasCargoReports(currentItem, reportIndex);

        if (isSelected(reportIndex, cargoIndex)) {
            // If a departure report is being deselected, also deselect all its cargo reports
            if (cargoIndex === undefined) {
                setSelectedItems(prev => 
                    prev.filter(item => item.reportIndex !== reportIndex)
                );
            } else {
                // Just deselect the specific cargo report
                setSelectedItems(prev => 
                    prev.filter(item => 
                        !(item.reportIndex === reportIndex && item.cargoIndex === cargoIndex)
                    )
                );
            }
        } else {
            // If selecting a departure report with cargo reports
            if (cargoIndex === undefined && hasCargoItems) {
                // Select the departure report itself
                const newSelection = { reportIndex };
                
                // Also select all its cargo reports
                const cargoSelections: SelectionItem[] = [];
                const cargoCount = currentItem.cargoReport.length;
                
                for (let i = 0; i < cargoCount; i++) {
                    cargoSelections.push({ reportIndex, cargoIndex: i });
                }
                
                // Add both the departure and all its cargo reports
                setSelectedItems(prev => {
                    // First remove any existing selections for this report index to avoid duplicates
                    const filtered = prev.filter(item => item.reportIndex !== reportIndex);
                    return [...filtered, newSelection, ...cargoSelections];
                });
            } else {
                // Add just the specific item (either a non-departure report or a specific cargo report)
                setSelectedItems(prev => [...prev, { reportIndex, cargoIndex }]);
            }
        }
    };

    const bunkerReports = Array.isArray(report.standaloneForm?.bunker?.bunkerReport)
        ? report.standaloneForm?.bunker?.bunkerReport
        : [];
    const hasStandaloneBunkerReports = bunkerReports.length > 0;

    const hasStandaloneSelection = () => {
        return (
            selectedStandaloneForms.delayOffHireNotice ||
            selectedStandaloneForms.statementOfFacts ||
            selectedStandaloneForms.bunker ||
            selectedStandaloneForms.bunkerIndices.length > 0
        );
    };

    const getStandaloneSelectionCount = () => {
        let count = 0;
        if (selectedStandaloneForms.delayOffHireNotice) count += 1;
        if (selectedStandaloneForms.statementOfFacts) count += 1;

        // For new bunker format count selected bunker reports, otherwise count bunker form itself.
        if (hasStandaloneBunkerReports) {
            const selectedBunkerReportCount = selectedStandaloneForms.bunker
                ? bunkerReports.length
                : selectedStandaloneForms.bunkerIndices.length;
            count += selectedBunkerReportCount;
        } else if (selectedStandaloneForms.bunker) {
            count += 1;
        }

        return count;
    };

    // Toggle standalone form selection
    const toggleStandaloneForm = (formType: keyof StandaloneFormSelection) => {
        setSelectedStandaloneForms(prev => ({
            ...prev,
            [formType]: !prev[formType]
        }));
    };

    const isStandaloneBunkerSelected = (index: number) => {
        return selectedStandaloneForms.bunker || selectedStandaloneForms.bunkerIndices.includes(index);
    };

    const toggleStandaloneBunkerSelection = (index: number) => {
        setSelectedStandaloneForms(prev => {
            if (prev.bunker) return prev; // If deleting all bunker reports, keep children locked.
            const isSelected = prev.bunkerIndices.includes(index);
            return {
                ...prev,
                bunkerIndices: isSelected
                    ? prev.bunkerIndices.filter(item => item !== index)
                    : [...prev.bunkerIndices, index]
            };
        });
    };

    // Select or deselect all cargo reports of a departure
    const toggleAllCargo = (departureIndex: number, cargoCount: number) => {
        const allSelected = areAllCargoSelected(departureIndex, cargoCount);
        const departureSelected = isSelected(departureIndex);
        
        // If the parent departure is selected, we can't modify cargo selections directly
        if (departureSelected) {
            // If departure is selected, the only option is to deselect it (which will deselect all cargo)
            setSelectedItems(prev => 
                prev.filter(item => item.reportIndex !== departureIndex)
            );
            return;
        }
        
        if (allSelected) {
            // Deselect all cargo reports
            setSelectedItems(prev => 
                prev.filter(item => 
                    !(item.reportIndex === departureIndex && item.cargoIndex !== undefined)
                )
            );
        } else {
            // Select all cargo reports
            const newSelections: SelectionItem[] = [];
            for (let i = 0; i < cargoCount; i++) {
                newSelections.push({ reportIndex: departureIndex, cargoIndex: i });
            }
            
            // Add new selections while keeping other selections
            setSelectedItems(prev => {
                const filtered = prev.filter(item => 
                    !(item.reportIndex === departureIndex && item.cargoIndex !== undefined)
                );
                return [...filtered, ...newSelections];
            });
        }
    };

    // Handle select all button
    const handleSelectAll = () => {
        if (selectedItems.length === (report.timeline || []).length) {
            setSelectedItems([]);
        } else {
            // Create selections for all reports
            const newSelections: SelectionItem[] = [];
            (report.timeline || []).forEach((_, index) => {
                newSelections.push({ reportIndex: index });
            });
            setSelectedItems(newSelections);
        }
    };

    // Process the submission
    const handleSubmit = () => {
        if (selectedItems.length === 0 && 
            !selectedStandaloneForms.delayOffHireNotice && 
            !selectedStandaloneForms.bunker &&
            selectedStandaloneForms.bunkerIndices.length === 0 &&
            !selectedStandaloneForms.statementOfFacts) {
            setError("Please select at least one item to delete");
            return;
        }

        // Separate regular report indices and cargo indices
        const reportIndicesToDelete: number[] = [];
        const cargoIndicesToDelete: { reportIndex: number; cargoIndex: number }[] = [];

        // Process selected items
        selectedItems.forEach(item => {
            if (item.cargoIndex !== undefined) {
                // This is a cargo report
                cargoIndicesToDelete.push({
                    reportIndex: item.reportIndex,
                    cargoIndex: item.cargoIndex
                });
            } else {
                // This is a regular report (or a departure report that includes cargo)
                reportIndicesToDelete.push(item.reportIndex);
            }
        });

        const shouldDeleteAllStandaloneBunker =
            selectedStandaloneForms.bunker ||
            (hasStandaloneBunkerReports &&
                bunkerReports.length > 0 &&
                selectedStandaloneForms.bunkerIndices.length === bunkerReports.length);

        const hasBunkerSelection =
            selectedStandaloneForms.bunker ||
            selectedStandaloneForms.bunkerIndices.length > 0;

        const bunkerSelectionPayload = hasStandaloneBunkerReports
            ? hasBunkerSelection
                ? {
                    deleteAll: shouldDeleteAllStandaloneBunker,
                    bunkerIndices: shouldDeleteAllStandaloneBunker ? [] : selectedStandaloneForms.bunkerIndices
                }
                : false
            : selectedStandaloneForms.bunker;

        // Send the deletion data
        onSubmit({
            reportIndicesToDelete,
            cargoIndicesToDelete,
            standaloneFormsToDelete: {
                delayOffHireNotice: selectedStandaloneForms.delayOffHireNotice,
                bunker: bunkerSelectionPayload,
                statementOfFacts: selectedStandaloneForms.statementOfFacts
            },
            backup: report // Send the actual report as backup
        });
    };

    // Check if any standalone forms exist
    const hasStandaloneForms = () => {
        return report.standaloneForm && (
            report.standaloneForm.delayOffHireNotice ||
            report.standaloneForm.bunker ||
            report.standaloneForm.statementOfFacts
        );
    };

    return (
        <div className="p-4">
            <h3 className="mb-4 text-lg font-medium text-gray-900">
                Delete Reports
            </h3>

            {/* Standalone Forms Section */}
            {hasStandaloneForms() && (
                <div className="mb-6">
                    <h4 className="mb-2 text-sm font-medium text-gray-700">
                        Standalone Forms
                    </h4>
                    <div className="space-y-2">
                        {report.standaloneForm?.delayOffHireNotice && (
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                    checked={selectedStandaloneForms.delayOffHireNotice}
                                    onChange={() => toggleStandaloneForm('delayOffHireNotice')}
                                />
                                <label className="ml-2 text-sm text-gray-700">
                                    Delay Off Hire Notice
                                </label>
                            </div>
                        )}
                        {report.standaloneForm?.bunker && !hasStandaloneBunkerReports && (
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                    checked={selectedStandaloneForms.bunker}
                                    onChange={() => toggleStandaloneForm('bunker')}
                                />
                                <label className="ml-2 text-sm text-gray-700">
                                    Bunker Form
                                </label>
                            </div>
                        )}
                        {report.standaloneForm?.bunker && hasStandaloneBunkerReports && (
                            <div className="border border-gray-100 rounded-md p-2 bg-gray-50">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                            checked={selectedStandaloneForms.bunker}
                                            onChange={() => toggleStandaloneForm("bunker")}
                                        />
                                        <label className="ml-2 text-sm text-gray-700">
                                            All Bunker Reports ({bunkerReports.length})
                                        </label>
                                    </div>
                                    <span className="text-xs text-gray-500">
                                        New format
                                    </span>
                                </div>
                                <div className="mt-2 space-y-1">
                                    {bunkerReports.map((bunker: any, bunkerIndex: number) => (
                                        <div
                                            key={`standalone-bunker-${bunkerIndex}`}
                                            className={classNames(
                                                "flex items-center rounded-md px-2 py-1.5",
                                                {
                                                    "bg-blue-50": isStandaloneBunkerSelected(bunkerIndex)
                                                }
                                            )}
                                        >
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                                checked={isStandaloneBunkerSelected(bunkerIndex)}
                                                disabled={selectedStandaloneForms.bunker}
                                                onChange={() => toggleStandaloneBunkerSelection(bunkerIndex)}
                                            />
                                            <label className="ml-2 text-xs text-gray-700">
                                                Bunker #{bunkerIndex + 1}
                                                {" - "}
                                                {bunker?.bunker_delivery_local_date || "-"}{" "}
                                                {bunker?.bunker_delivery_local_time || ""}{" "}
                                                {bunker?.bunker_delivery_zd || ""}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {report.standaloneForm?.statementOfFacts && (
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                    checked={selectedStandaloneForms.statementOfFacts}
                                    onChange={() => toggleStandaloneForm('statementOfFacts')}
                                />
                                <label className="ml-2 text-sm text-gray-700">
                                    Statement of Facts
                                </label>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Report Selection */}
            <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">
                        Select Reports to Delete
                    </label>
                    <button
                        onClick={handleSelectAll}
                        className="text-sm text-blue-600 hover:text-blue-800"
                    >
                        {selectedItems.length === (report.timeline || []).length
                            ? "Deselect All"
                            : "Select All"}
                    </button>
                </div>
                
                {/* Selection behavior explanation */}
                <div className="p-2 mb-2 text-xs bg-gray-50 border border-gray-100 rounded-md">
                    <p className="font-medium text-gray-700">Selection behavior:</p>
                    <ul className="mt-1 pl-4 list-disc text-gray-600">
                        <li>Selecting a departure report will automatically select all its cargo reports</li>
                        <li>You can select individual cargo reports without selecting their parent departure report</li>
                    </ul>
                </div>
                
                <div className="border rounded-md divide-y">
                    {(report.timeline || []).map((item, index) => {
                        const isDeparture = item.report_type === "DEPARTURE";
                        const hasCargoItems = hasCargoReports(item, index);

                        return (
                            <React.Fragment key={index}>
                                <div
                                    className={classNames(
                                        "flex items-center p-3 hover:bg-gray-50",
                                        {
                                            "bg-blue-50": isSelected(index),
                                            "border-b border-gray-100": hasCargoItems && isExpanded(index)
                                        }
                                    )}
                                >
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                        checked={isSelected(index)}
                                        onChange={() => toggleSelection(index)}
                                    />
                                    <div className="ml-3 flex-grow">
                                        <p className="text-sm font-medium text-gray-900">
                                            {item.report_type}
                                            {hasCargoItems && (
                                                <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                                                    {item.cargoReport.length} cargo report{item.cargoReport.length > 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            {item.to_local_date} {item.to_local_time}
                                        </p>
                                    </div>
                                    {hasCargoItems && (
                                        <button
                                            type="button"
                                            onClick={() => toggleExpand(index)}
                                            className="ml-2 text-gray-500 hover:text-gray-700"
                                        >
                                            <span className="sr-only">
                                                {isExpanded(index) ? "Collapse" : "Expand"}
                                            </span>
                                            <svg 
                                                className={`h-5 w-5 transition-transform ${isExpanded(index) ? 'rotate-180' : ''}`} 
                                                fill="currentColor" 
                                                viewBox="0 0 20 20"
                                            >
                                                <path 
                                                    fillRule="evenodd" 
                                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" 
                                                    clipRule="evenodd" 
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                
                                {/* Render cargo reports if this is an expanded departure report */}
                                {hasCargoItems && isExpanded(index) && (
                                    <div className="bg-gray-50 pl-6 py-2 border-b">
                                        <div className="flex items-center justify-between px-3 py-1">
                                            <span className="text-xs font-medium text-gray-700">
                                                Cargo Reports ({item.cargoReport.length})
                                                {isSelected(index) && (
                                                    <span className="ml-2 text-blue-600 text-xs">
                                                        - All selected with departure
                                                    </span>
                                                )}
                                            </span>
                                            <button
                                                onClick={() => toggleAllCargo(index, item.cargoReport.length)}
                                                className={classNames(
                                                    "text-xs",
                                                    {
                                                        "text-blue-600 hover:text-blue-800": !isSelected(index),
                                                        "text-red-600 hover:text-red-800": isSelected(index)
                                                    }
                                                )}
                                            >
                                                {isSelected(index) 
                                                    ? "Deselect Departure & Cargo" 
                                                    : areAllCargoSelected(index, item.cargoReport.length)
                                                        ? "Deselect All Cargo"
                                                        : "Select All Cargo"
                                                }
                                            </button>
                                        </div>
                                        
                                        {/* List of cargo reports */}
                                        <div className="mt-1 space-y-1">
                                            {item.cargoReport.map((cargo: any, cargoIndex: number) => {
                                                // Determine if this cargo is selected because parent is selected
                                                const isParentSelected = isSelected(index) && !isSelected(index, cargoIndex);
                                                
                                                return (
                                                    <div 
                                                        key={`cargo-${index}-${cargoIndex}`}
                                                        className={classNames(
                                                            "flex items-center px-3 py-2 hover:bg-gray-100 rounded-md ml-2",
                                                            {
                                                                "bg-blue-50": isSelected(index, cargoIndex),
                                                                "bg-blue-50/50": isParentSelected // lighter blue when selected via parent
                                                            }
                                                        )}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className={classNames(
                                                                "w-4 h-4 border-gray-300 rounded",
                                                                {
                                                                    "text-blue-600": !isParentSelected,
                                                                    "text-blue-400": isParentSelected
                                                                }
                                                            )}
                                                            checked={isSelected(index, cargoIndex) || isParentSelected}
                                                            disabled={isParentSelected}
                                                            onChange={() => toggleSelection(index, cargoIndex)}
                                                        />
                                                        {isParentSelected && (
                                                            <span className="ml-2 text-xs text-blue-500 font-medium">
                                                                (Selected with departure)
                                                            </span>
                                                        )}
                                                        <div className="ml-3">
                                                            <p className="text-xs font-medium text-gray-900">
                                                                CARGO
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {cargo.cargo_cargo_table ? `${cargo.cargo_cargo_table.length} cargo items` : 
                                                                cargo.cargo_info_table ? `${cargo.cargo_info_table.length} vehicle types` :
                                                                cargo.cargo_loaded_containers_table ? `${cargo.cargo_loaded_containers_table.length} loaded containers` : 
                                                                'Cargo data'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {error && (
                <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-md">
                    {error}
                </div>
            )}

            {/* Warning Message */}
            {(selectedItems.length > 0 || hasStandaloneSelection()) && (
                <div className="p-3 mb-4 text-sm text-yellow-700 bg-yellow-100 rounded-md">
                    {(() => {
                        // Count report items and cargo items
                        const reportCount = selectedItems.filter(item => item.cargoIndex === undefined).length;
                        const cargoCount = selectedItems.filter(item => item.cargoIndex !== undefined).length;
                        const standaloneFormsCount = getStandaloneSelectionCount();
                        
                        const parts = [];
                        if (reportCount > 0) parts.push(`${reportCount} report(s)`);
                        if (cargoCount > 0) parts.push(`${cargoCount} cargo report(s)`);
                        if (standaloneFormsCount > 0) {
                            if (hasStandaloneBunkerReports && (selectedStandaloneForms.bunker || selectedStandaloneForms.bunkerIndices.length > 0)) {
                                const selectedBunkerReportsCount = selectedStandaloneForms.bunker
                                    ? bunkerReports.length
                                    : selectedStandaloneForms.bunkerIndices.length;
                                const nonBunkerStandaloneFormsCount =
                                    (selectedStandaloneForms.delayOffHireNotice ? 1 : 0) +
                                    (selectedStandaloneForms.statementOfFacts ? 1 : 0);

                                if (selectedBunkerReportsCount > 0) {
                                    parts.push(`${selectedBunkerReportsCount} bunker report(s)`);
                                }
                                if (nonBunkerStandaloneFormsCount > 0) {
                                    parts.push(`${nonBunkerStandaloneFormsCount} standalone form(s)`);
                                }
                            } else {
                                parts.push(`${standaloneFormsCount} standalone form(s)`);
                            }
                        }
                        
                        return `Warning: This action will delete ${parts.join(' and ')} and cannot be undone.`;
                    })()}
                    A backup will be created before deletion.
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
                <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={selectedItems.length === 0 && !hasStandaloneSelection()}
                    className={classNames(
                        "px-4 py-2 text-sm font-medium text-white rounded-md",
                        {
                            "bg-red-600 hover:bg-red-700": selectedItems.length > 0 || hasStandaloneSelection(),
                            "bg-gray-300 cursor-not-allowed": selectedItems.length === 0 && !hasStandaloneSelection(),
                        }
                    )}
                >
                    Delete Selected
                </button>
            </div>
        </div>
    );
};

export default DeleteReportForm; 